import pluralize from "pluralize";

// util
import { ZonedMoment } from "./ZonedMoment";
/**
 * Relative time.
 */
const MILLIS_IN = {
  ONE_SECOND: 1000,
  ONE_MINUTE: 60000,
  ONE_HOUR: 3600000,
  ONE_DAY: 86400000,
  ONE_WEEK: 604800000,
  ONE_MONTH: 2628000000,
  ONE_YEAR: 31540000000,
};

function formatRelativeTime(time: number, unit: string, isPast: boolean, abbreviate?: boolean) {
  const value = abbreviate ? `${time}${unit}` : pluralize(unit, time, true);
  return `${value} ${isPast ? "from now" : "ago"}`;
}

export function getRelativeTimeMillis(time: number, abbreviate?: boolean) {
  return getRelativeTime(new ZonedMoment(time), abbreviate);
}

export function getRelativeTime(time: ZonedMoment, abbreviate?: boolean) {
  const now = new ZonedMoment();
  // + => future, - => past
  const diffInMillis = time.diff(now);

  if (Math.abs(diffInMillis) < MILLIS_IN.ONE_SECOND) {
    // less than one second
    return "just now";
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_MINUTE) {
    // less than one minute
    const seconds = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_SECOND);
    return formatRelativeTime(
      seconds,
      `${abbreviate ? "s" : "second"}`,
      diffInMillis > 0,
      abbreviate,
    );
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_HOUR) {
    // less than one hour
    const minutes = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_MINUTE);
    return formatRelativeTime(
      minutes,
      `${abbreviate ? "min" : "minute"}`,
      diffInMillis > 0,
      abbreviate,
    );
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_DAY) {
    // less than one day
    const hours = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_HOUR);
    return formatRelativeTime(hours, `${abbreviate ? "hr" : "hour"}`, diffInMillis > 0, abbreviate);
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_WEEK) {
    // less than one week
    const days = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_DAY);
    if (days === 1) {
      return `${diffInMillis > 0 ? "tomorrow" : "yesterday"}`;
    }
    return formatRelativeTime(days, `${abbreviate ? "d" : "day"}`, diffInMillis > 0, abbreviate);
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_MONTH) {
    // less than one month
    const weeks = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_WEEK);
    return formatRelativeTime(weeks, `${abbreviate ? "wk" : "week"}`, diffInMillis > 0, abbreviate);
  } else if (Math.abs(diffInMillis) < MILLIS_IN.ONE_YEAR) {
    // less than one year
    const months = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_MONTH);
    return formatRelativeTime(
      months,
      `${abbreviate ? "mo" : "month"}`,
      diffInMillis > 0,
      abbreviate,
    );
  } else {
    const years = Math.round(Math.abs(diffInMillis) / MILLIS_IN.ONE_YEAR);
    return formatRelativeTime(years, `${abbreviate ? "yr" : "year"}`, diffInMillis > 0, abbreviate);
  }
}

///////////////////
// Absolute Time //
///////////////////

function formatAbsoluteTime(startTime: string, endTime?: string) {
  if (!endTime) {
    return startTime;
  }

  return `${startTime} – ${endTime}`;
}

export function getAbsoluteDate(
  startTime: ZonedMoment,
  endTime?: ZonedMoment,
  showDay?: boolean,
  forceShowYear?: boolean,
) {
  const now = new ZonedMoment();
  const showYear = forceShowYear || !startTime.isSame(endTime || now, "y");

  const formattedStart = startTime.format(
    `${showDay ? "ddd" : ""} MMM D${showYear ? ", YYYY" : ""}`,
  );

  if (!endTime || startTime.isSame(endTime, "d")) {
    return formatAbsoluteTime(formattedStart);
  }

  const formattedEnd = endTime.format(`${showDay ? "ddd" : ""} MMM D${showYear ? ", YYYY" : ""}`);
  return formatAbsoluteTime(formattedStart, formattedEnd);
}
